import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useReducedMotion } from '../../hooks/useReducedMotion';

const hasWebGL = (canvas: HTMLCanvasElement): boolean => {
  const context = canvas.getContext('webgl2') || canvas.getContext('webgl');
  return Boolean(context);
};

type ProbeNode = {
  mesh: import('three').Mesh;
  ring: import('three').Line;
  distance: number;
  velocity: number;
  phase: number;
};

export const RadialCore = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const reducedMotion = useReducedMotion();
  const [fallback, setFallback] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !hasWebGL(canvas)) {
      setFallback(true);
      return;
    }

    let disposed = false;
    let frameId = 0;

    const initialize = async () => {
      const THREE = await import('three');
      if (disposed) {
        return;
      }

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
      camera.position.set(0, 0, 8);

      const renderer = new THREE.WebGLRenderer({
        canvas,
        antialias: true,
        alpha: true,
        powerPreference: 'high-performance'
      });
      renderer.setPixelRatio(Math.min(1.6, window.devicePixelRatio));

      const group = new THREE.Group();
      scene.add(group);

      const ambient = new THREE.AmbientLight(0x8eff65, 0.7);
      const key = new THREE.PointLight(0xb7ff6a, 20, 20, 1.5);
      key.position.set(3, 2, 6);
      const rim = new THREE.PointLight(0x58f7b6, 15, 22, 1.2);
      rim.position.set(-3, -2, 5);
      scene.add(ambient, key, rim);

      const coreMaterial = new THREE.MeshPhysicalMaterial({
        color: 0x0c1717,
        emissive: 0x152a16,
        emissiveIntensity: 0.55,
        transparent: true,
        opacity: 0.88,
        roughness: 0.32,
        metalness: 0.55
      });
      const coreGeometry = new THREE.IcosahedronGeometry(1.2, 2);
      const coreMesh = new THREE.Mesh(coreGeometry, coreMaterial);
      group.add(coreMesh);

      const shellGeometry = new THREE.TorusGeometry(1.85, 0.045, 16, 100);
      const shellMaterial = new THREE.MeshBasicMaterial({
        color: 0x8bff3e,
        transparent: true,
        opacity: 0.75
      });
      const shell = new THREE.Mesh(shellGeometry, shellMaterial);
      group.add(shell);

      const shellTwoGeometry = new THREE.TorusGeometry(2.25, 0.025, 16, 120);
      const shellTwoMaterial = new THREE.MeshBasicMaterial({
        color: 0x58f7b6,
        transparent: true,
        opacity: 0.35
      });
      const shellTwo = new THREE.Mesh(shellTwoGeometry, shellTwoMaterial);
      shellTwo.rotation.x = Math.PI / 2;
      group.add(shellTwo);

      const probes: ProbeNode[] = [];
      for (let index = 0; index < 16; index += 1) {
        const marker = new THREE.Mesh(
          new THREE.SphereGeometry(0.042, 10, 10),
          new THREE.MeshBasicMaterial({ color: index % 2 ? 0xb7ff6a : 0x58f7b6 })
        );

        const ringGeometry = new THREE.BufferGeometry().setFromPoints([
          new THREE.Vector3(0, 0, 0),
          new THREE.Vector3(0, 0, 0)
        ]);
        const ring = new THREE.Line(
          ringGeometry,
          new THREE.LineBasicMaterial({
            color: 0xb7ff6a,
            transparent: true,
            opacity: 0.3
          })
        );

        group.add(marker);
        group.add(ring);

        probes.push({
          mesh: marker,
          ring,
          distance: 2.2 + Math.random() * 1,
          velocity: 0.16 + Math.random() * 0.17,
          phase: Math.random() * Math.PI * 2
        });
      }

      const pointer = { x: 0, y: 0 };
      const target = { x: 0, y: 0 };
      let paused = false;

      const onMove = (event: PointerEvent) => {
        pointer.x = (event.clientX / window.innerWidth) * 2 - 1;
        pointer.y = (event.clientY / window.innerHeight) * 2 - 1;
      };

      const onResize = () => {
        const width = canvas.clientWidth;
        const height = canvas.clientHeight;
        if (!width || !height) {
          return;
        }
        renderer.setSize(width, height, false);
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
      };

      const onVisibility = () => {
        paused = document.hidden;
      };

      window.addEventListener('resize', onResize);
      window.addEventListener('pointermove', onMove, { passive: true });
      document.addEventListener('visibilitychange', onVisibility);
      onResize();

      const updateProbes = (time: number) => {
        probes.forEach((probe, index) => {
          const theta = time * probe.velocity + probe.phase + index * 0.2;
          const x = Math.cos(theta) * probe.distance;
          const y = Math.sin(theta * 0.9) * 0.8;
          const z = Math.sin(theta) * probe.distance * 0.55;
          probe.mesh.position.set(x, y, z);

          const positions = probe.ring.geometry.getAttribute('position');
          positions.setXYZ(0, 0, 0, 0);
          positions.setXYZ(1, x, y, z);
          positions.needsUpdate = true;
        });
      };

      const renderStatic = () => {
        updateProbes(2.5);
        group.rotation.y = 0.42;
        group.rotation.x = -0.2;
        renderer.render(scene, camera);
      };

      if (reducedMotion) {
        renderStatic();
      } else {
        const minFrameDuration = 1000 / 48;
        let previousTime = 0;

        const animate = (raw: number) => {
          if (paused) {
            frameId = window.requestAnimationFrame(animate);
            return;
          }

          if (raw - previousTime < minFrameDuration) {
            frameId = window.requestAnimationFrame(animate);
            return;
          }

          previousTime = raw;
          const t = raw * 0.001;

          target.x += (pointer.x - target.x) * 0.05;
          target.y += (pointer.y - target.y) * 0.05;

          camera.position.x = target.x * 0.45;
          camera.position.y = -target.y * 0.32;
          camera.lookAt(0, 0, 0);

          coreMesh.rotation.x = -t * 0.14;
          coreMesh.rotation.y = t * 0.22;
          coreMaterial.emissiveIntensity = 0.42 + Math.sin(t * 2.4) * 0.12;
          shell.rotation.z = t * 0.28;
          shellMaterial.opacity = 0.58 + Math.sin(t * 1.9) * 0.14;
          shellTwo.rotation.y = -t * 0.2;
          shellTwoMaterial.opacity = 0.26 + Math.sin(t * 1.5) * 0.1;
          group.rotation.y = t * 0.08;

          updateProbes(t);
          renderer.render(scene, camera);
          frameId = window.requestAnimationFrame(animate);
        };

        frameId = window.requestAnimationFrame(animate);
      }

      return () => {
        window.removeEventListener('resize', onResize);
        window.removeEventListener('pointermove', onMove);
        document.removeEventListener('visibilitychange', onVisibility);

        if (frameId) {
          window.cancelAnimationFrame(frameId);
        }

        probes.forEach((probe) => {
          probe.mesh.geometry.dispose();
          (probe.mesh.material as import('three').Material).dispose();
          probe.ring.geometry.dispose();
          (probe.ring.material as import('three').Material).dispose();
        });

        coreGeometry.dispose();
        coreMaterial.dispose();
        shellGeometry.dispose();
        shellMaterial.dispose();
        shellTwoGeometry.dispose();
        shellTwoMaterial.dispose();
        renderer.dispose();
      };
    };

    let cleanup: (() => void) | undefined;
    initialize().then((dispose) => {
      cleanup = dispose;
    });

    return () => {
      disposed = true;
      cleanup?.();
    };
  }, [reducedMotion]);

  if (fallback) {
    return (
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(139,255,62,0.25),transparent_58%)]" aria-hidden="true" />
    );
  }

  return (
    <div className="absolute inset-0">
      <canvas ref={canvasRef} className="h-full w-full" aria-hidden="true" />
      <motion.div
        className="pointer-events-none absolute inset-[16%] rounded-full border border-brand-lime/40"
        animate={reducedMotion ? undefined : { rotate: 360 }}
        transition={{ duration: 20, ease: 'linear', repeat: Infinity }}
      />
      <motion.div
        className="pointer-events-none absolute inset-[27%] rounded-full border border-brand-aqua/35"
        animate={reducedMotion ? undefined : { rotate: -360 }}
        transition={{ duration: 15, ease: 'linear', repeat: Infinity }}
      />
      <motion.div
        className="pointer-events-none absolute inset-[36%] rounded-full border border-brand-lime/35"
        animate={reducedMotion ? undefined : { opacity: [0.2, 0.8, 0.25] }}
        transition={{ duration: 2.2, repeat: Infinity }}
      />
      <motion.div
        className="pointer-events-none absolute inset-0 bg-[conic-gradient(from_45deg_at_50%_50%,transparent,rgba(183,255,106,0.22),transparent_42%)] mix-blend-screen"
        animate={reducedMotion ? undefined : { rotate: 360, opacity: [0.2, 0.45, 0.2] }}
        transition={{ duration: 10.8, repeat: Infinity, ease: 'linear' }}
      />
    </div>
  );
};
